/**
 * ReferralShareCard — rich one-stop share panel for Referral Partners.
 * Lives only on the Referral Partner Dashboard (per founder decision).
 *
 *  • Copy Referral Link / Copy Referral Code
 *  • WhatsApp / Facebook / Telegram share (pre-filled Urdu/Hinglish message)
 *  • QR Code (via api.qrserver.com, no extra dep) + Download
 *  • Funnel stats: Total Link Clicks → Signups → Trial Accounts → Paid Shops → Conversion Rate
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import {
  Copy, MessageCircle, Facebook, Send, Download, MousePointerClick,
  Users, Hourglass, Award, Percent, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import NativeShareButton from "./NativeShareButton";
import GooglePlayBadge, { PLAY_STORE_URL } from "./GooglePlayBadge";

const SHARE_MSG = (link, code, planPrice = 1500, referralReward = 500) =>
  `Assalam-o-Alaikum! 👋

Agar aap Mobile Shop, Electronics, Furniture ya kisi bhi Installment Business se taluq rakhte hain, HisaabPro zaroor try karein.

💳 Subscription: sirf PKR ${planPrice.toLocaleString()} — WhatsApp verification ke baad foran activate.

Register using my referral link:
${link}

Referral Code: ${code}

📱 Download the Android App:
https://play.google.com/store/apps/details?id=com.myhisaabpro.app`;

function StatTile({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  };
  return (
    <div className={`rounded-md border ${tones[tone]} p-3`} data-testid={testid}>
      <Icon className="w-4 h-4 opacity-70" />
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mt-1">{label}</div>
      <div className="font-display text-xl sm:text-2xl font-bold tabular mt-0.5">{value}</div>
    </div>
  );
}

export default function ReferralShareCard({ code, link, referralCode, referralLink, commissionRate = 30, planPrice = 1500, referralRewardFixed = 500, stats: statsProp = null }) {
  // Support both prop shapes: legacy {code,link} and new {referralCode,referralLink}
  const finalCode = referralCode || code;
  const finalLink = referralLink || link;
  const [stats, setStats] = useState(statsProp);

  useEffect(() => {
    if (statsProp) { setStats(statsProp); return; }
    api.get("/referrals/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [statsProp]);

  const msg = useMemo(() => SHARE_MSG(finalLink, finalCode, planPrice, referralRewardFixed), [finalLink, finalCode, planPrice, referralRewardFixed]);
  const qrUrl = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=2&data=${encodeURIComponent(finalLink || "")}`,
    [finalLink],
  );

  if (!finalCode || !finalLink) return null;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const openShare = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const onWA = () => openShare(`https://wa.me/?text=${encodeURIComponent(msg)}`);
  const onFB = () => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(finalLink)}&quote=${encodeURIComponent(msg)}`);
  const onTG = () => openShare(`https://t.me/share/url?url=${encodeURIComponent(finalLink)}&text=${encodeURIComponent(msg)}`);

  const downloadQR = async () => {
    try {
      const r = await fetch(qrUrl);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hisaabpro-referral-${code}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("QR downloaded");
    } catch (_) {
      toast.error("Could not download QR");
    }
  };

  return (
    <div className="rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 sm:p-6" data-testid="share-referral-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Refer &amp; Earn</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-0.5">Share Referral</h2>
          <p className="text-sm text-slate-600 mt-1">One-tap share — har qualifying paid subscription par <b className="text-emerald-700">PKR {referralRewardFixed.toLocaleString()} fixed reward</b>.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Code</div>
          <div className="font-mono font-bold text-xl sm:text-2xl text-slate-900" data-testid="share-code">{finalCode}</div>
        </div>
      </div>

      {/* Link + copy actions */}
      <div className="mt-4 rounded-md bg-white border border-slate-200 px-3 py-2.5 flex items-center gap-2 overflow-hidden">
        <span className="font-mono text-xs text-slate-700 truncate flex-1" data-testid="share-link">{finalLink}</span>
        <button
          onClick={() => copy(finalLink, "Link")}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-md border border-slate-200 px-2.5 py-1.5 hover:bg-slate-50"
          data-testid="share-copy-link"
        >
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>

      {/* Share buttons */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        <NativeShareButton
          title="Refer & Earn — HisaabPro"
          text={msg}
          url={finalLink}
          label="Share via…"
          variant="default"
          size="default"
          className="bg-slate-900 hover:bg-slate-800 text-white col-span-2 sm:col-span-1"
          testid="share-native"
        />
        <Button onClick={onWA} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="share-wa">
          <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
        </Button>
        <Button onClick={onFB} className="bg-sky-700 hover:bg-sky-800 text-white" data-testid="share-fb">
          <Facebook className="w-4 h-4 mr-1.5" /> Facebook
        </Button>
        <Button onClick={onTG} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="share-tg">
          <Send className="w-4 h-4 mr-1.5" /> Telegram
        </Button>
        <Button onClick={() => copy(finalCode, "Code")} variant="outline" className="border-slate-300" data-testid="share-copy-code">
          <Copy className="w-4 h-4 mr-1.5" /> Copy Code
        </Button>
      </div>

      {/* QR + message preview */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-md border border-slate-200 bg-white p-3 flex flex-col items-center" data-testid="share-qr-card">
          <img src={qrUrl} alt="Referral QR Code" className="w-32 h-32 sm:w-36 sm:h-36" data-testid="share-qr-img" />
          <Button size="sm" onClick={downloadQR} variant="outline" className="mt-2 w-full" data-testid="share-qr-download">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download QR
          </Button>
        </div>
        <div className="sm:col-span-2 rounded-md border border-slate-200 bg-white p-3" data-testid="share-message-preview">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
            <QrCode className="w-3 h-3" /> Pre-filled message
          </div>
          <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed font-sans max-h-40 overflow-y-auto">{msg}</pre>
        </div>
      </div>

      {/* Android app CTA — customers can also install directly from Play Store */}
      <div className="mt-4 rounded-md border border-slate-200 bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="share-android-cta">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Android App</div>
          <div className="text-sm text-slate-700 mt-0.5">
            Customer ko batayein — <b>HisaabPro Google Play</b> par bhi available hai.
          </div>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-block text-[11px] text-slate-500 hover:text-slate-800 break-all"
            data-testid="share-play-link"
          >
            play.google.com/store/apps/details?id=com.myhisaabpro.app
          </a>
        </div>
        <GooglePlayBadge size="md" testId="share-play-badge" />
      </div>

      {/* Funnel stats */}
      {stats && (
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Referral Funnel</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            <StatTile icon={MousePointerClick} label="Total Link Clicks" value={stats.link_clicks} tone="slate" testid="share-stat-clicks" />
            <StatTile icon={Users} label="Total Signups" value={stats.signups} tone="sky" testid="share-stat-signups" />
            <StatTile icon={Hourglass} label="Trial Accounts" value={stats.trial_accounts} tone="amber" testid="share-stat-trial" />
            <StatTile icon={Award} label="Paid Shops" value={stats.paid_shops} tone="emerald" testid="share-stat-paid" />
            <StatTile icon={Percent} label="Conversion Rate" value={`${stats.conversion_rate}%`} tone="indigo" testid="share-stat-conv" />
          </div>
        </div>
      )}
    </div>
  );
}
